import type { ReactNode } from 'react'
import cx from 'classnames'
import { RawButton } from '@/components/Button'
import { Icon, type IconName } from '@/components/Icon'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import { Tooltip } from '@/components/Tooltip'
import styles from './SidebarMenu.module.css'

export interface SidebarMenuProps {
  title: string
  /**
   * `SidebarMenuItem`s.
   */
  children?: ReactNode
  /**
   * Shown under the items, like an add button.
   */
  footer?: ReactNode
}

/**
 * The pages of an editor tab along its left edge, to pick the one to show.
 */
export function SidebarMenu({ title, children, footer }: SidebarMenuProps) {
  return (
    <nav className={styles.root} aria-label={title}>
      <div className={styles.title}>{title}</div>
      <div className={styles.items}>{children}</div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </nav>
  )
}

export interface SidebarMenuItemProps {
  title: string
  iconName: IconName
  isSelected?: boolean
  /**
   * Marks the item as having a problem, explained by its tooltip on hover
   * and keyboard focus.
   */
  error?: string
  onPress?: () => unknown
  testId?: string
}

/**
 * A page in a {@link SidebarMenu}.
 *
 * Not `SplitViewSidebarListItem`: that's a 68px title-and-subtitle row with
 * no error state, while the editor's menu needs compact icon-and-title rows
 * that mark the pages with errors.
 */
export function SidebarMenuItem({
  title,
  iconName,
  isSelected,
  error,
  onPress,
  testId,
}: SidebarMenuItemProps) {
  const item = (
    <RawButton
      className={cx(
        styles.item,
        isSelected && styles.selected,
        error && styles.invalid,
      )}
      aria-current={isSelected ? 'page' : undefined}
      onPress={onPress}
      testId={testId}
    >
      <Icon
        name={iconName}
        size={16}
        className={styles.itemIcon}
        aria-hidden="true"
      />
      <span className={styles.itemTitle}>{title}</span>
      {error && (
        <span className={styles.itemError}>
          <Icon name="status-alert" size={14} aria-hidden="true" />
          <span className="sr-only">{error}</span>
        </span>
      )}
    </RawButton>
  )
  return error ? <Tooltip content={error}>{item}</Tooltip> : item
}

/**
 * A skeleton for a {@link SidebarMenuItem}.
 */
export function SidebarMenuItemSkeleton() {
  const { vary } = useSkeletonListContext()
  return (
    <div className={styles.item}>
      <span className={styles.itemIcon}>
        <Skeleton variant="rect" width={16} height={16} />
      </span>
      <span className={styles.itemTitle}>
        <Skeleton width={vary(100)} />
      </span>
    </div>
  )
}

export interface SidebarMenuEmptyProps {
  children?: ReactNode
}

/**
 * Says that a {@link SidebarMenu} has no items yet.
 */
export function SidebarMenuEmpty({ children }: SidebarMenuEmptyProps) {
  return <div className={styles.empty}>{children}</div>
}
